import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

import { AdminIcon } from "@/components/admin/icon";
import { AuthBrand } from "@/components/auth/auth-brand";
import {
	extractErrorMessage,
	useForgotPasswordMutation,
} from "@/hooks/use-auth-api";

const schema = z.object({
	email: z.string().email("Adresse e-mail invalide"),
});

type FormValues = z.infer<typeof schema>;

export function ForgotPasswordPage() {
	const navigate = useNavigate();
	const mutation = useForgotPasswordMutation();
	const [error, setError] = useState<string | null>(null);
	const [sentTo, setSentTo] = useState<string | null>(null);

	const {
		register,
		handleSubmit,
		formState: { errors, isSubmitting },
	} = useForm<FormValues>({
		resolver: zodResolver(schema),
		defaultValues: { email: "" },
	});

	const submit = handleSubmit(async (data) => {
		setError(null);
		try {
			await mutation.mutateAsync({ email: data.email });
			setSentTo(data.email);
		} catch (err) {
			setError(extractErrorMessage(err, "Envoi impossible"));
		}
	});

	return (
		<div className="auth-page">
			<AuthBrand />
			<main className="auth-panel">
				<div className="auth-form-wrap">
					{sentTo ? (
						<>
							<div className="auth-h">
								<h2>Email envoyé</h2>
								<p>
									Si un compte existe pour <strong>{sentTo}</strong>, vous
									recevrez un lien de réinitialisation dans les prochaines
									minutes.
								</p>
							</div>
							<div className="auth-alert info" data-testid="forgot-sent">
								<span className="icon">
									<AdminIcon n="info" s={16} />
								</span>
								<div>
									<strong>Lien valide 60 minutes</strong>
									Le lien ne peut être utilisé qu'une seule fois. Vérifiez votre
									boîte de réception (et les spams).
								</div>
							</div>
							<button
								type="button"
								className="btn-auth-ghost"
								style={{ marginTop: 8 }}
								onClick={() => navigate("/login")}
							>
								<AdminIcon n="chevL" s={14} /> Retour à la connexion
							</button>
						</>
					) : (
						<>
							<div className="auth-h">
								<h2>Mot de passe oublié ?</h2>
								<p>
									Saisissez votre email professionnel. Nous vous enverrons un
									lien sécurisé valable <strong>1 heure</strong>.
								</p>
							</div>

							{error && (
								<div className="auth-alert danger" data-testid="forgot-error">
									<span className="icon">
										<AdminIcon n="alert" s={16} />
									</span>
									<div>{error}</div>
								</div>
							)}

							<form onSubmit={submit} noValidate>
								<div className="field">
									<div className="field-label">Email professionnel</div>
									<div
										className={
											"field-input-wrap " + (errors.email ? "error" : "")
										}
									>
										<span className="input-prefix">
											<AdminIcon n="mail" s={16} c="var(--a-ink-mute)" />
										</span>
										<input
											type="email"
											placeholder="vous@akwaba.cm"
											autoFocus
											data-testid="forgot-email"
											{...register("email")}
										/>
									</div>
									{errors.email && (
										<div className="field-help error">
											<AdminIcon n="alert" s={12} />
											{errors.email.message}
										</div>
									)}
								</div>

								<button
									type="submit"
									className="btn-submit"
									disabled={isSubmitting}
									data-testid="forgot-submit"
								>
									{isSubmitting ? "Envoi…" : "Envoyer le lien"}
									<AdminIcon n="arrow" s={16} c="#fff" />
								</button>
								<button
									type="button"
									className="btn-auth-ghost"
									style={{ marginTop: 8 }}
									onClick={() => navigate("/login")}
								>
									<AdminIcon n="chevL" s={14} /> Retour à la connexion
								</button>
							</form>
						</>
					)}
				</div>
			</main>
		</div>
	);
}
